<div
  class="ai-chat-form__root"
  [class.ai-chat-form__root--with-history]="isFullPage()"
>
  @if (showHistoryPanel()) {
    <ai-chat-history-panel
      class="ai-chat-form__history"
      [class.ai-chat-form__history--collapsed]="historySidebarCollapsed()"
    />
  }

  <div class="ai-chat-form__main">
    <div
      class="header"
      [class.ai-chat-form__header--expand-rail]="isFullPage() && historySidebarCollapsed()"
      [class.ai-chat-form__header--title-only]="isFullPage() && showTitleBlock()"
    >
      @if (isFullPage() && chatHeaderContexts().length) {
        <nav
          class="ai-chat-form__tabs"
          role="tablist"
        >
          @for (context of chatHeaderContexts(); track context) {
            <button
              type="button"
              role="tab"
              class="ai-chat-form__tab"
              [class.ai-chat-form__tab--active]="tabNav!.activeContext() === context"
              [attr.aria-selected]="tabNav!.activeContext() === context"
              (click)="navigateToChatContext(context)"
            >
              <span class="ai-chat-form__tab-label">{{ chatContextLabelKey(context) | translateIn }}</span>
            </button>
          }
        </nav>
      }

      @if (isFullPage() && tabNav?.activeContext() === AiChatContext.Work) {
        @if (selectedChatBadge(); as badge) {
          <div class="ai-chat-form__work-thread-head">
            @if (popupChatTitle(); as title) {
              <span class="ai-chat-form__work-thread-title">{{ title }}</span>
            }
            <span class="ai-chat-form__work-thread-badge">{{ badge }}</span>
          </div>
        }
      }

      @if (isFullPage() && historySidebarCollapsed()) {
        <button
          type="button"
          class="history-sidebar-expand"
          puButton
          uiType="quaternary"
          size="m"
          [onlyIcon]="true"
          [attr.aria-label]="'chatBot.history.expandSidebar' | translateIn"
          (click)="expandHistorySidebar()"
        >
          <pu-icon iconName="pu-chevron-right" size="20" />
        </button>
      }

      @if (showTitleBlock()) {
        @if (!isFullPage() && popupHistoryOpen()) {
          <button
            type="button"
            class="ai-chat-form__history-back"
            puButton
            size="m"
            uiType="quaternary"
            [onlyIcon]="true"
            [attr.aria-label]="'chatBot.history.back' | translateIn"
            (click)="closePopupHistory()"
          >
            <pu-icon iconName="pu-chevron-left" size="20" />
          </button>
          <h4 class="header-text ai-chat-form__history-title">
            {{ 'chatBot.history.chatHistory' | translateIn }}
          </h4>
        } @else {
          <div class="ai-chat-form__header-title-block">
            <h4 class="header-text">
              {{ 'aiChatBotName' | translateIn }}
            </h4>
            @if (isFullPage() && selectedChatBadge(); as badge) {
              <span class="ai-chat-form__header-badge">{{ badge }}</span>
            }
          </div>
        }
        @if (!isFullPage()) {
          <div class="ai-chat-form__header-actions">
            <button
              class="close"
              type="button"
              puButton
              size="m"
              uiType="quaternary"
              [onlyIcon]="true"
              (click)="close()"
            >
              <pu-icon iconName="close" size="16" />
            </button>
          </div>
        }
      }

    </div>

    @if (!isFullPage() && chatHeaderContexts().length) {
      <nav
        class="ai-chat-form__tabs ai-chat-form__tabs--popup"
        role="tablist"
      >
        @for (context of chatHeaderContexts(); track context) {
          <button
            type="button"
            role="tab"
            class="ai-chat-form__tab"
            [class.ai-chat-form__tab--active]="tabNav!.activeContext() === context"
            [attr.aria-selected]="tabNav!.activeContext() === context"
            (click)="navigateToChatContext(context)"
          >
            <span class="ai-chat-form__tab-label">{{ chatContextLabelKey(context) | translateIn }}</span>
          </button>
        }
      </nav>
    }

    @if (!isFullPage() && !popupHistoryOpen()) {
      <div class="ai-chat-form__popup-history-bar">
        <div class="ai-chat-form__popup-chat-info">
          <span class="ai-chat-form__popup-chat-name">
            {{ popupChatTitle() ?? ('chatBot.history.newChat' | translateIn) }}
          </span>
          @if (selectedChatBadge(); as badge) {
            <span class="ai-chat-form__popup-work-context-badge" [title]="badge">{{ badge }}</span>
          }
        </div>
        <button
          type="button"
          class="ai-chat-form__popup-history-btn"
          (click)="openPopupHistory()"
        >
          {{ 'chatBot.history.title' | translateIn }}
        </button>
      </div>
    }

    @if (!isFullPage() && popupHistoryOpen()) {
      <ai-chat-history-panel
        [isPopup]="true"
        (backClick)="closePopupHistory()"
      />
    }

    <div
      #messagesBody
      class="body messages"
      [class.hidden]="!isFullPage() && popupHistoryOpen()"
      (scroll)="onMessagesScroll()"
    >
      @if (showChatCtas()) {
        <fb-cta-list
          class="ai-chat-form__chat-ctas"
          layout="wrap"
          [ctas]="chatCtas()"
          [surface]="isFullPage() ? CtaSurface.FullPage : CtaSurface.Popup"
          [disabled]="disableSendQuestion()"
          (ctaSelected)="onChatCtaSelected($event)"
        />
      }

      @if (tabsAuthSessionError()) {
        <div class="ai-chat-form__error">
          {{ 'chatBot.error.sessionExpired' | translateIn }}
        </div>
      } @else if (tabsSystemError()) {
        <div class="ai-chat-form__error">
          {{ 'chatBot.error.text' | translateIn }}
        </div>
      }

      @if (authSessionError()) {
        <div class="ai-chat-form__error">
          {{ 'chatBot.error.sessionExpired' | translateIn }}
        </div>
      } @else if (systemError()) {
        <div class="ai-chat-form__error">
          {{ 'chatBot.error.text' | translateIn }}
        </div>
      }

      @for (message of messages(); track message.id) {
        <div class="message">
          @if (message.role === ChatMessageRole.User) {
            <user-chat-message
              [text]="message.content"
              [time]="message.createdAt"
            />
          } @else {
            <bot-chat-message
              [message]="agentMsgToDisplay(message)"
              [isFirst]="$first"
              [feedbackEligible]="!message.isStreaming && message.isFeedbackEligible !== false"
            />
          }
        </div>
      }

      @if (chatLoading()) {
        <ng-container *puSpinner="true"/>
      }

      @if (
        !chatLoading()
        && messages().length === 0
        && !tabsInitError()
        && !isWorkContextPickerOpen()
      ) {
        @if (!chatsLoading() && chats().length === 0) {
          <div class="ai-chat-form__empty">
            <span class="ai-chat-form__empty__text">
              {{ 'chatBot.noChatsEmpty' | translateIn }}
            </span>
          </div>
        } @else if (!chatsLoading()) {
          @let welcomeText = welcomeGreetingKey() | translateIn;
          <div class="message">
            <bot-chat-message
              [message]="welcomeMessageView(welcomeText)"
              [isFirst]="true"
            />
          </div>
        }
      }
    </div>
    @if (isFullPage() || !popupHistoryOpen()) {
    <div class="actions">
      @if (feedbackMessageId()) {
    <form [formGroup]="feedbackForm" class="feedback-form" (ngSubmit)="leaveFeedback()">
      <div class="feedback-form-header">
        {{ 'feedbackForm.header' | translateIn }}
      </div>

      <div class="reason">
        @for (reason of dislikeReasons; track reason.value) {
          <pu-radio-option
            class="reason-control"
            formControlName="feedbackType"
            controlId="feedbackType"
            [option]="reason"
          />
          }
            </div>

            <pu-text-area
              class="feedback"
              formControlName="text"
              [placeholder]="'feedbackForm.textArea.placeholder' | translateIn"
              [rows]="4"
              [resizeDisabled]="true"
              [maxLength]="feedbackCommentMaxLength"
            />

            <div class="feedback-form-buttons">
              <button
                type="submit"
                puButton
                size="l"
                uiType="primary"
              >{{ 'chatBot.buttons.leaveFeedback' | translateIn }}</button>

            <button
              type="button"
              puButton
              size="l"
              uiType="secondary"
              (click)="closeFeedbackForm()"
            >{{ 'chatBot.buttons.cancel' | translateIn }}</button>
          </div>
        </form>
            } @else {
            <form [formGroup]="chatQuestionForm" class="question-form" (ngSubmit)="send()">
              <pu-text-area
                class="question question-textarea"
                formControlName="question"
                [rows]="1"
                [resizeDisabled]="true"
                [placeholder]="'questionForm.input.placeholder' | translateIn"
                fbChatTextareaAutosize
                (keydown)="onQuestionKeydown($event)"
              />

              <button
                type="submit"
                puButton
                size="l"
                uiType="primary"
                [disabled]="disableSendQuestion()"
              >{{ 'chatBot.buttons.send' | translateIn }}</button>
            </form>
          }
      </div>
    }

  </div>

</div>
